import {
  Control,
  FieldErrors,
  useFieldArray,
  Controller,
} from "react-hook-form";
import { TCreateCOSHH } from "../types";
import { Button, Icon, Input } from "~/components/ui";
import { FormSelectInput } from "~/components/form-inputs";
import { useGetChemicalRegisterEntriesQuery } from "~/redux/api/registers";
import { cn } from "~/lib/utils";

interface ChemicalsStepProps {
  control: Control<TCreateCOSHH>;
  errors: FieldErrors<TCreateCOSHH>;
}

const ChemicalsStep = ({ control, errors }: ChemicalsStepProps) => {
  const {
    fields: chemicals,
    append: appendChemical,
    remove: removeChemical,
  } = useFieldArray({
    control,
    name: "chemicals",
  });

  const { data: chemicalsRes } = useGetChemicalRegisterEntriesQuery({});
  const chemicalOptions =
    chemicalsRes?.result?.data?.map((c: any) => ({
      label: c.productName,
      value: c.id,
    })) || [];

  return (
    <div className="space-y-6 rounded-xl border border-neutral-200 bg-white p-8">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-Medium text-primary-500">Chemicals</h2>
          <p className="text-sm text-neutral-500">
            Identify chemicals, usage details, and assess risk levels.
          </p>
        </div>
        <Button
          type="button"
          onClick={() =>
            appendChemical({
              chemicalRegisterEntryId: { label: "", value: "" },
              routeOfExposure: "",
              quantityUsed: "",
              frequencyOfUse: "",
            })
          }
          className="flex items-center gap-2"
        >
          <Icon name="Plus" className="h-4 w-4" />
          Add Chemical
        </Button>
      </div>

      <div className="space-y-6">
        {chemicals.map((chem, index) => (
          <div
            key={chem.id}
            className="border border-neutral-200 rounded-lg p-4 bg-neutral-50 relative space-y-4"
          >
            <button
              type="button"
              onClick={() => removeChemical(index)}
              className="absolute top-4 right-4 text-neutral-400 hover:text-red-500 transition-colors z-10"
            >
              <Icon name="Trash2" className="h-4 w-4" />
            </button>
            <h3 className="font-medium text-sm mb-4">Chemical {index + 1}</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Controller
                  control={control}
                  name={`chemicals.${index}.chemicalRegisterEntryId`}
                  render={({ field: { onChange, value } }) => (
                    <FormSelectInput
                      label="Chemical Product"
                      options={chemicalOptions}
                      placeholder="Select a chemical"
                      isMulti={false}
                      value={value || undefined}
                      onChange={onChange}
                      errors={{
                        error:
                          !!errors.chemicals?.[index]?.chemicalRegisterEntryId,
                        message:
                          errors.chemicals?.[
                            index
                          ]?.chemicalRegisterEntryId?.message?.toString(),
                      }}
                    />
                  )}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Route of Exposure</label>
                <Controller
                  control={control}
                  name={`chemicals.${index}.routeOfExposure`}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="e.g. Inhalation"
                      className={cn(
                        errors.chemicals?.[index]?.routeOfExposure &&
                          "border-destructive",
                      )}
                    />
                  )}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Quantity Used</label>
                <Controller
                  control={control}
                  name={`chemicals.${index}.quantityUsed`}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="e.g. 5 Liters"
                      className={cn(
                        errors.chemicals?.[index]?.quantityUsed &&
                          "border-destructive",
                      )}
                    />
                  )}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Frequency of Use</label>
                <Controller
                  control={control}
                  name={`chemicals.${index}.frequencyOfUse`}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="e.g. Daily"
                      className={cn(
                        errors.chemicals?.[index]?.frequencyOfUse &&
                          "border-destructive",
                      )}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        ))}

        {chemicals.length === 0 && (
          <div className="text-center py-10 border-2 border-dashed border-neutral-200 rounded-lg">
            <p className="text-neutral-500 mb-4">No chemicals added yet.</p>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                appendChemical({
                  chemicalRegisterEntryId: { label: "", value: "" },
                  routeOfExposure: "",
                  quantityUsed: "",
                  frequencyOfUse: "",
                })
              }
            >
              Add First Chemical
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChemicalsStep;
